import { Button, Input, Modal, Text, Toast } from '@byecode/ui'
import type { ApplicationSettingAuthentication, DevicePreviewEnum } from '@lighthouse/core'
import { clone } from 'rambda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AgreementCheckbox, ConfirmContent } from '../AgreementCheckbox'
import { Code } from '../Code'
import { LOGIN_AUTH_CODE_MAP } from '../constant'
import { useFormRuleMap, useSendCode } from '../hooks'
import LoginBox from '../LoginBox'
import * as CM from '../styles'
import { type AccountLoginForm, type AppLoginMode } from '../types'

const SCxInput = styled(Input)`
    &:focus-within {
        border-color: var(--color-app-main);
    }
`

interface LoginProps {
    type: 'email' | 'mobile'
    authentication: ApplicationSettingAuthentication
    disableEvent?: boolean
    language: string
    previewType?: DevicePreviewEnum
    agreedToTerms?: boolean
    enabledLoginMethodsCount?: number
    onHomeBack?: () => void
    onChangeLanguage: (v: string) => void
    onChangeMode: (v: AppLoginMode) => void
    onLogin?: (v: AccountLoginForm) => void
    onSendCode?: (v: 'email' | 'mobile', sendCodeInfo: string) => Promise<boolean> | undefined
    onAgreedToTermsChange?: (agreed: boolean) => void
}

export const Login: React.FunctionComponent<LoginProps> = ({
    type,
    authentication,
    disableEvent,
    language,
    previewType,
    agreedToTerms = false,
    enabledLoginMethodsCount = 0,
    onHomeBack,
    onChangeLanguage,
    onChangeMode,
    onSendCode,
    onLogin,
    onAgreedToTermsChange
}) => {
    const { login, name, logo, describe } = authentication
    const { requireAgree } = login
    const methods = useForm<AccountLoginForm>({
        mode: 'onChange',
        shouldFocusError: false,
        defaultValues: {
            email: '',
            code: '',
            mobile: '',
            authType: LOGIN_AUTH_CODE_MAP[type]
        }
    })
    const { t } = useTranslation()

    // 判断是否需要在当前页面显示协议组件（只有一种登录方式）
    const shouldShowAgreement = useMemo(() => {
        return enabledLoginMethodsCount === 1 && requireAgree?.enable
    }, [enabledLoginMethodsCount, requireAgree?.enable])

    const formRuleMap = useFormRuleMap()

    const { register, handleSubmit, getValues, watch } = methods
    const info = useMemo(
        () =>
            ({
                email: { placeholder: t('emailUrl') },
                mobile: { placeholder: t('mobileCode') }
            }[type]),
        [t, type]
    )

    const [openedCode, setOpenedCode] = useState(false)

    const handleGetCodeHandle = useCallback(
        async (resolve?: () => void, reject?: () => void) => {
            setOpenedCode(true)
            const result = await onSendCode?.(type, getValues(type))
            return result ? resolve?.() : reject?.()
        },
        [getValues, onSendCode, type]
    )

    const { state, getPhoneCode } = useSendCode(type, handleGetCodeHandle)

    const handleGetPhoneCode = useCallback(async () => {
        // 检查协议同意状态
        if (requireAgree?.enable && !agreedToTerms) {
            const isConfirm = await Modal.confirm({
                title: '用户协议及隐私条款',
                content: <ConfirmContent authentication={authentication} />,
                okText: '同意',
                okStatus: 'primary',
                okProps: { primaryColor: 'var(--color-app-main)' }
            })

            if (!isConfirm) {
                return
            }

            onAgreedToTermsChange?.(true)
        }

        if (!formRuleMap[type].pattern.value.test(getValues(type))) {
            Toast.error(formRuleMap[type].pattern.message)
            return
        }

        !disableEvent && getPhoneCode()
    }, [
        formRuleMap,
        type,
        getValues,
        requireAgree?.enable,
        agreedToTerms,
        disableEvent,
        getPhoneCode,
        authentication,
        onAgreedToTermsChange
    ])

    useEffect(() => {
        const { unsubscribe } = watch(value => {
            if (value.code?.length === 6) {
                onLogin?.(clone(value) as AccountLoginForm)
            }
        })
        return unsubscribe
    }, [handleSubmit, onLogin, watch])

    const ele = useMemo(() => {
        if (openedCode) {
            return (
                <Code
                    type={type}
                    account={getValues(type)}
                    authentication={authentication}
                    label={state.codeText}
                    disabled={state.codeStep > 0 || state.disable}
                    language={language}
                    previewType={previewType}
                    onChangeLanguage={onChangeLanguage}
                    onSendCode={getPhoneCode}
                    onBack={() => {
                        setOpenedCode(false)
                    }}
                />
            )
        }
        return (
            <LoginBox
                name={name}
                logo={logo}
                describe={describe}
                language={language}
                previewType={previewType}
                onChangeLanguage={onChangeLanguage}
                onBack={() => onChangeMode('home')}
            >
                <SCxInput
                    {...register(type, formRuleMap[type])}
                    style={{ height: 44, borderRadius: 10 }}
                    placeholder={info.placeholder}
                    onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                />

                {/* 只有一种登录方式时显示协议组件 */}
                {shouldShowAgreement && onAgreedToTermsChange && (
                    <AgreementCheckbox authentication={authentication} checked={agreedToTerms} onChange={onAgreedToTermsChange} />
                )}

                <Button
                    block
                    style={{ height: 44, marginTop: 16, backgroundColor: 'var(--color-app-main)', borderStyle: 'none' }}
                    type="primary"
                    radius={10}
                    disabled={state.codeStep > 0 || state.disable}
                    onClick={handleGetPhoneCode}
                >
                    {state.codeText}
                </Button>
                <Button
                    style={{ height: 44, marginTop: 16, backgroundColor: 'var(--color-app-main-tint)', borderStyle: 'none' }}
                    onClick={onHomeBack}
                    radius={10}
                    block
                >
                    <Text size={16} color="var(--color-app-main)">
                        {t('goHome')}
                    </Text>
                </Button>
            </LoginBox>
        )
    }, [
        authentication,
        describe,
        formRuleMap,
        getPhoneCode,
        getValues,
        handleGetPhoneCode,
        info.placeholder,
        language,
        logo,
        name,
        onChangeLanguage,
        onChangeMode,
        onHomeBack,
        openedCode,
        previewType,
        register,
        state.codeStep,
        state.codeText,
        state.disable,
        t,
        type,
        shouldShowAgreement,
        agreedToTerms,
        onAgreedToTermsChange
    ])

    return (
        <CM.FormContainer>
            <FormProvider {...methods}>{ele}</FormProvider>
        </CM.FormContainer>
    )
}
